import { useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useQuickServiceDetail,
  useUpdateQuickServiceStatusDirect,
  useQuickServiceTimeline,
} from "../../../hooks/useCraneQueries.js";
import CraneRecoveryExpandedPanel from "../../../Component/Workshop/QuickServices/CraneRecoveryExpandedPanel.jsx";
import { useQueryClient } from "@tanstack/react-query";
import "../../../Component/Workshop/QuickServices/CraneRecoveryExpandedPanel.css";
import "./QuickServicesPage.css";
import "../../../Component/Workshop/QuickServices/ServiceRequestcard.css";

import {
  Avatar,
  Spinner,
  ErrorState,
  Modal,
} from "../../../Component/Workshop/UI/index";
import { BackIcon } from "../../../Component/Workshop/UI/icons";

// ─── Status config ─────────────────────────────────────────────────────────

const STATUS_ORDER = [
  "NEW", "DETAILS_PENDING", "VEHICLE_SELECTED", "ASSIGNED", "MOVED",
  "REACHED_AT_PICKUP", "VEHICLE_INSPECTED", "OTP_VERIFIED", "WORKING",
  "DONE", "CANCELLED",
];

function hasReached(current, target) {
  return STATUS_ORDER.indexOf(current) >= STATUS_ORDER.indexOf(target);
}

const STATUS_LABEL = {
  NEW:               "New",
  DETAILS_PENDING:   "Details pending",
  VEHICLE_SELECTED:  "Vehicle selected",
  ASSIGNED:          "Assigned",
  MOVED:             "En route",
  REACHED_AT_PICKUP: "Reached pickup",
  VEHICLE_INSPECTED: "Vehicle inspected",
  OTP_VERIFIED:      "OTP verified",
  WORKING:           "Working",
  DONE:              "Done",
  CANCELLED:         "Cancelled",
};

// Maps to the same variant strings used by Badge in CaseDetailPage
const STATUS_BADGE_VARIANT = {
  NEW:               "default",
  DETAILS_PENDING:   "warning",
  VEHICLE_SELECTED:  "warning",
  ASSIGNED:          "info",
  MOVED:             "info",
  REACHED_AT_PICKUP: "info",
  VEHICLE_INSPECTED: "info",
  OTP_VERIFIED:      "info",
  WORKING:           "info",
  DONE:              "success",
  CANCELLED:         "danger",
};

const VEHICLE_TYPE_LABEL = {
  hydra_crane:          "Hydra crane",
  recovery_non_flatbed: "Recovery van (non-flatbed)",
  recovery_flatbed:     "Recovery van (flatbed)",
};

// ─── Jump phases (phase strip, replaces StageBar) ───────────────────────────

const JUMP_PHASES = [
  { key: "phase-1",  label: "Incident" },
  { key: "phase-2",  label: "Owner & driver" },
  { key: "phase-3",  label: "Vehicle" },
  { key: "phase-4",  label: "Assignment" },
  { key: "phase-4b", label: "Payment", isPayment: true },
  { key: "phase-5",  label: "Dispatch" },
];

// ─── Detail-page tabs ────────────────────────────────────────────────────────

const DETAIL_TABS = [
  { key: "details",  label: "Details" },
  { key: "timeline", label: "Timeline" },
];

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} d ago`;
}

// ─── Pill style helper ───────────────────────────────────────────────────────
// Inline pill that matches the wd-card dark theme — bigger than Badge's
// default 11px, proper border-radius, no reliance on Badge internals.

const PILL_VARIANTS = {
  default: {
    background: "rgba(255,255,255,0.06)",
    color:       "var(--wd-t2)",
    border:      "1px solid var(--wd-border)",
  },
  info: {
    background: "rgba(33,166,233,0.12)",
    color:       "#38bdf8",
    border:      "1px solid rgba(33,166,233,0.28)",
  },
  warning: {
    background: "rgba(234,179,8,0.12)",
    color:       "#facc15",
    border:      "1px solid rgba(234,179,8,0.28)",
  },
  success: {
    background: "rgba(34,197,94,0.12)",
    color:       "#4ade80",
    border:      "1px solid rgba(34,197,94,0.25)",
  },
  danger: {
    background: "rgba(239,68,68,0.1)",
    color:       "#f87171",
    border:      "1px solid rgba(239,68,68,0.25)",
  },
};

function pillStyle(variant = "default") {
  const v = PILL_VARIANTS[variant] || PILL_VARIANTS.default;
  return {
    display:      "inline-flex",
    alignItems:   "center",
    gap:          4,
    padding:      "4px 11px",
    borderRadius: 20,
    fontSize:     13,
    fontWeight:   500,
    lineHeight:   1,
    whiteSpace:   "nowrap",
    ...v,
  };
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function QuickServiceDetailPage() {
  const { requestCode } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateStatusDirect = useUpdateQuickServiceStatusDirect(requestCode);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [tab, setTab] = useState("details");

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [directStatus, setDirectStatus]       = useState("");
  const [statusReason, setStatusReason]       = useState("");
  const [statusError, setStatusError]         = useState("");

  const { data: request, isLoading, isError, refetch } = useQuickServiceDetail(requestCode);

  const handleRefreshAll = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      // 1. Refetch page-level query
      await refetch();

      // 2. Invalidate any react-query data scoped to this request
      await queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some(
            (part) => typeof part === "string" && part.includes(requestCode)
          ),
      });

      // 3. Tell child panels (whose live data comes from WebSocket, not
      //    react-query) to manually resync as a fallback
      setRefreshSignal((n) => n + 1);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refetch, queryClient, requestCode]);

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    setDirectStatus("");
    setStatusReason("");
    setStatusError("");
  };

  const handleDirectStatusUpdate = async () => {
    if (!directStatus) return;
    if (!statusReason.trim()) {
      setStatusError("Please provide a reason for this status change.");
      return;
    }
    setStatusError(null);
    try {
      await updateStatusDirect.mutateAsync({
        status: directStatus,
        reason: statusReason.trim(),
      });
      await refetch();
      closeStatusModal();
    } catch (err) {
      console.error("Direct status update failed", err);
      setStatusError(err?.response?.data?.message || "Failed to update status. Please retry.");
    }
  };

  const handleBack = () =>
    navigate("/admin/quick-services", {
      state: {
        activeTab: request?.serviceType === "recovery" ? "recovery" : "crane",
        highlightedCase: requestCode,
      },
    });
  const handleNavigateVendors = () => {
    if (!request) return;
    navigate(`/admin/vendors?requestCode=${requestCode}&serviceType=${request.serviceType}`);
  };
  const handleTrack = () => navigate(`/admin/quick-services?track=${requestCode}`);

  if (isLoading) return <Spinner full />;
  if (isError || !request) return (
    <ErrorState message="Case not found" onRetry={refetch} />
  );

  const isQS = request.serviceType === "crane" || request.serviceType === "recovery";
  const raw  = request._raw || request;

  return (
    <div className="wd-animate" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Breadcrumb — identical structure to CaseDetailPage ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>

        {/* Left group: breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={handleBack}>
            <BackIcon /> Back
          </button>
          <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>/</span>
          <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>Quick services</span>
          <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>/</span>
          <span style={{ fontFamily: "var(--wd-mono)", fontSize: 13, color: "var(--wd-blue)", fontWeight: 600 }}>
            {requestCode}
          </span>
          {raw.accidentCaseNo && (
            <>
              <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>·</span>
              <button
                className="wd-btn wd-btn-ghost wd-btn-sm"
                onClick={() =>
                  navigate(`/admin/cases/${raw.accidentCaseNo}`, {
                    state: { activeTab: "On-Spot Services" },
                  })
                }
              >
                ↗ Case {raw.accidentCaseNo}
              </button>
            </>
          )}
        </div>

        {/* Right group: update status + refresh */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setStatusModalOpen(true)}>
            Update Status
          </button>

          <button
            className="wd-btn wd-btn-ghost wd-btn-sm"
            onClick={handleRefreshAll}
            disabled={isRefreshing}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
            title="Force refresh if live updates seem stuck"
          >
            <span style={{ display: "inline-block", animation: isRefreshing ? "wd-spin 0.8s linear infinite" : "none" }}>
              🔄
            </span>
            {isRefreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* ── Header card — mirrors CaseDetailPage's wd-card header ── */}
      <div className="wd-card" style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>

          {/* Left: avatar + identity */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Avatar name={raw.customerName || raw.driverName || "?"} size={48} />
            <div>
              {/* Code + badges row */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "var(--wd-mono)", fontSize: 18, fontWeight: 800, color: "var(--wd-blue)" }}>
                  {raw.requestCode}
                </span>
                {/* Service type pill */}
                <span style={pillStyle("default")}>
                  {raw.supportVehicleType
                    ? VEHICLE_TYPE_LABEL[raw.supportVehicleType] || raw.supportVehicleType
                    : raw.serviceType === "crane" ? "Crane" : "Recovery"}
                </span>
                {/* Status pill */}
                <span style={pillStyle(STATUS_BADGE_VARIANT[raw.status] || "default")}>
                  {STATUS_LABEL[raw.status] || raw.status}
                </span>
                {/* Urgent pill */}
                {raw.urgent && (
                  <span style={pillStyle("danger")}>⚠ Urgent</span>
                )}
              </div>

              {/* Sub-line 1: name · phone · plate */}
              <div style={{ marginTop: 5, color: "var(--wd-t4)", fontSize: 16, fontWeight: 700 }}>
                {[raw.customerName || raw.driverName, raw.driverContact, raw.vehicleNumber]
                  .filter(Boolean).join(" · ")}
              </div>

              {/* Sub-line 2: route · time */}
              <div style={{ marginTop: 4, color: "var(--wd-t3)", fontSize: 12 }}>
                {(raw.pickupLocation || raw.dropLocation) && (
                  <>📍 {[raw.pickupLocation, raw.dropLocation].filter(Boolean).join(" → ")}</>
                )}
                {raw.createdAt && ` · ${timeAgo(raw.createdAt)}`}
              </div>
            </div>
          </div>

          {/* Right: action buttons */}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            {isQS && hasReached(raw.status, "ASSIGNED") && (
              <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={handleTrack}>
                📍 Live track
              </button>
            )}
            {raw.status !== "DONE" && raw.status !== "CANCELLED" && (
              <button
                className="wd-btn wd-btn-sm"
                style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "var(--wd-danger, #ef4444)" }}
                onClick={() =>
                  document.querySelector(".qs-expanded-actions")
                    ?.scrollIntoView({ behavior: "smooth", block: "center" })
                }
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* ── Phase strip — replaces StageBar, lives inside header card ── */}
        {isQS && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--wd-border)" }}>
            <PhaseStrip request={request} />
          </div>
        )}
      </div>

      {/* ── Detail / Timeline tabs ── */}
      <div className="qs-tabs">
        {DETAIL_TABS.map((t) => (
          <button
            key={t.key}
            className={`qs-tab ${tab === t.key ? "qs-tab--active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "details" && (
        <>
          {/* ── Panels ── */}
          {isQS && (
            <CraneRecoveryExpandedPanel
              request={request}
              key={`${requestCode}-${refreshSignal}`}    // ← forces remount on refresh
              onNavigateVendors={handleNavigateVendors}
              onTrack={handleTrack}
            />
          )}

          {request.serviceType === "advocate" && (
            <UnwiredNotice
              serviceType="advocate"
              note="Advocate case page isn't wired yet — it needs vendor and document fields the detail endpoint doesn't return yet. Use the list page for now."
            />
          )}

          {!isQS && request.serviceType !== "advocate" && (
            <UnwiredNotice
              serviceType={request.serviceType || "this service"}
              note="This service type doesn't have a dedicated case page yet."
            />
          )}
        </>
      )}

      {tab === "timeline" && <TimelineTab requestCode={requestCode} />}

      {/* ── Direct status-update modal ── */}
      <Modal
        open={statusModalOpen}
        onClose={closeStatusModal}
        title="Update Case Status"
        footer={
          <>
            <button className="wd-btn wd-btn-ghost" onClick={closeStatusModal}>
              Cancel
            </button>
            <button
              className="wd-btn wd-btn-primary"
              onClick={handleDirectStatusUpdate}
              disabled={!directStatus || updateStatusDirect.isPending}
            >
              {updateStatusDirect.isPending ? "Updating…" : "Update"}
            </button>
          </>
        }
      >
        <div>
          <label className="wd-form-label">New Status</label>
          <select
            className="wd-input wd-select"
            value={directStatus}
            onChange={(e) => {
              setDirectStatus(e.target.value);
              setStatusError(null);
            }}
          >
            <option value="">Select status...</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>

        {/* Heads-up when the jump skips intermediate workflow steps — those
            steps' side effects (notifications, invoice, WebSocket pushes to
            vendor/driver apps) won't run automatically. */}
        {directStatus &&
          STATUS_ORDER.indexOf(directStatus) > STATUS_ORDER.indexOf(raw.status) + 1 && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 12,
                background: "rgba(234,179,8,.08)",
                border: "1px solid rgba(234,179,8,.2)",
                color: "#facc15",
              }}
            >
              ⚠️ This skips intermediate steps (vendor assignment, OTP, payment,
              inspection). Side effects tied to those steps — notifications,
              invoice generation, WebSocket pushes to the vendor app — won't
              fire automatically.
            </div>
          )}

        <div style={{ marginTop: 12 }}>
          <label className="wd-form-label">Reason *</label>
          <textarea
            className="wd-input"
            rows={3}
            style={{ resize: "vertical" }}
            value={statusReason}
            onChange={(e) => {
              setStatusReason(e.target.value);
              setStatusError(null);
            }}
            placeholder="Explain why the status is being changed directly (required)..."
          />
        </div>

        {statusError && (
          <div style={{ marginTop: 8, color: "var(--wd-red, #ef4444)", fontSize: 12 }}>
            {statusError}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Timeline tab ────────────────────────────────────────────────────────────
// Reads quick_service_status_timeline via useQuickServiceTimeline. Mirrors
// AdvocateCaseDetailPage's TimelineTab layout (dot + connector + label +
// description + actor/time), with an extra pill for direct overrides and an
// old→new status line when the event actually changed status.

function TimelineTab({ requestCode }) {
  const { data: timeline = [], isLoading, isError, refetch } = useQuickServiceTimeline(requestCode);

  if (isLoading) {
    return (
      <div className="wd-card" style={{ padding: 32, textAlign: "center", color: "var(--wd-t3)" }}>
        Loading timeline…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="wd-card" style={{ padding: 32, textAlign: "center", color: "var(--wd-t3)" }}>
        Couldn't load timeline.{" "}
        <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  if (!timeline.length) {
    return (
      <div className="wd-card" style={{ padding: 32, textAlign: "center", color: "var(--wd-t3)" }}>
        No history yet.
      </div>
    );
  }

  const list = [...timeline].reverse(); // most recent first, like legal's TimelineTab

  return (
    <div className="wd-card" style={{ padding: 24 }}>
      {list.map((e, i) => (
        <div key={e.id ?? i} style={{ display: "flex", gap: 14 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: e.is_direct_override ? "#f97316" : "var(--wd-blue)",
                flexShrink: 0,
                marginTop: 4,
              }}
            />
            {i < list.length - 1 && <div className="wd-tl-line" />}
          </div>
          <div style={{ paddingBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}>
                {(e.event_type || "").replace(/_/g, " ")}
              </span>
              {e.old_status && e.new_status && e.old_status !== e.new_status && (
                <span style={{ fontSize: 11, color: "var(--wd-t3)" }}>
                  {e.old_status.replace(/_/g, " ")} → {e.new_status.replace(/_/g, " ")}
                </span>
              )}
              {!!e.is_direct_override && (
                <span style={pillStyle("warning")}>Direct override</span>
              )}
            </div>
            {e.description && (
              <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--wd-t2)" }}>
                {e.description}
              </p>
            )}
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 3 }}>
              {e.changed_by || "ADMIN"} · {new Date(e.created_at).toLocaleString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Phase strip ─────────────────────────────────────────────────────────────
// Visually replaces StageBar. Same layout as the stage label row in
// CaseDetailPage (justify-content: space-between, uppercase labels, blue/grey
// colouring) but with dots and a connecting progress line.

function PhaseStrip({ request }) {
  const raw    = request._raw || request;
  const status = raw.status;
  const paymentConfirmed = !!raw.customerAcceptedVendor;

  const [activeKey, setActiveKey] = useState(null);

  const phaseDoneMap = {
    "phase-1":  true,
    "phase-2":  hasReached(status, "VEHICLE_SELECTED"),
    "phase-3":  hasReached(status, "ASSIGNED"),
    "phase-4":  hasReached(status, "ASSIGNED"),
    "phase-4b": paymentConfirmed,
    "phase-5":  status === "DONE" || status === "CANCELLED",
  };

  const phaseLockedMap = {
    "phase-1":  false,
    "phase-2":  false,
    "phase-3":  !hasReached(status, "DETAILS_PENDING"),
    "phase-4":  !hasReached(status, "VEHICLE_SELECTED"),
    "phase-4b": !hasReached(status, "ASSIGNED"),
    "phase-5":  !hasReached(status, "ASSIGNED"),
  };

  const handleJump = (key) => {
    setActiveKey(key);
    const phaseBlocks = document.querySelectorAll(".cr-phase");
    const idx = JUMP_PHASES.findIndex((p) => p.key === key);
    phaseBlocks[idx]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, overflowX: "auto" }}>
      {JUMP_PHASES.map((phase, idx) => {
        const done   = phaseDoneMap[phase.key];
        const locked = phaseLockedMap[phase.key];
        const isWarn = phase.isPayment && !done && !locked;
        const isActive = activeKey === phase.key;

        // Dot colour — mirrors CaseDetailPage stage label colour logic
        const dotBg    = done   ? "var(--wd-success, #22c55e)"
                       : isWarn ? "rgba(234,179,8,0.15)"
                       : locked ? "var(--wd-surface)"
                                : "var(--wd-blue)";
        const dotBorder = done   ? "var(--wd-success, #22c55e)"
                        : isWarn ? "#d97706"
                        : locked ? "var(--wd-border)"
                                 : "var(--wd-blue)";
        const dotColor  = done   ? "#fff"
                        : isWarn ? "#d97706"
                        : locked ? "var(--wd-t3)"
                                 : "#fff";

        const labelColor = done   ? "var(--wd-success, #22c55e)"
                         : isWarn ? "#d97706"
                         : locked ? "var(--wd-t3)"
                                  : "var(--wd-blue)";

        return (
          <div key={phase.key} style={{ display: "flex", alignItems: "center", flex: idx < JUMP_PHASES.length - 1 ? 1 : 0 }}>
            {/* Phase item */}
            <div
              onClick={() => handleJump(phase.key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleJump(phase.key)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                gap: 5, cursor: locked ? "default" : "pointer",
                opacity: locked ? 0.45 : 1, flexShrink: 0,
              }}
            >
              {/* Dot */}
              <div style={{
                width: 22, height: 22, borderRadius: "50%",
                background: dotBg,
                border: `1.5px solid ${dotBorder}`,
                color: dotColor,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 700,
              }}>
                {done ? "✓" : isWarn ? "!" : idx + 1}
              </div>

              {/* Label — same style as CaseDetailPage stage labels */}
              <span style={{
                fontSize: 10, fontWeight: 600, textTransform: "uppercase",
                letterSpacing: ".05em", whiteSpace: "nowrap",
                color: isActive ? "var(--wd-blue)" : labelColor,
                textDecoration: isActive ? "underline" : "none",
              }}>
                {phase.label}
              </span>
            </div>

            {/* Connector line — mirrors StageBar's filled/unfilled track */}
            {idx < JUMP_PHASES.length - 1 && (
              <div style={{
                flex: 1, height: 2, margin: "0 4px",
                marginBottom: 18, /* align with dot centre */
                background: done ? "var(--wd-success, #22c55e)" : "var(--wd-border)",
                borderRadius: 1,
                transition: "background 0.2s",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Unwired notice ───────────────────────────────────────────────────────────

function UnwiredNotice({ serviceType, note }) {
  return (
    <div className="wd-card" style={{ padding: 40, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 28 }}>📭</span>
      <span style={{ color: "var(--wd-t2)", fontSize: 14, fontWeight: 500 }}>
        No dedicated page for {serviceType} yet
      </span>
      <span style={{ color: "var(--wd-t3)", fontSize: 12 }}>{note}</span>
    </div>
  );
}